/**
 * AgentStatsRegion — the always-visible transparency region of the agent
 * panel: live Todo block + Context stats line (plan §A.1).
 *
 * Todo has TWO sources, because the engines report progress differently:
 *   - builtin  → `episodes.todo`, read via useEpisodeTodo.
 *   - claude / codex → their own TaskCreate/TaskUpdate calls, mapped out of
 *     the stored episode todo record (D-4/D-6, plan split-2).
 *
 * The region used to be builtin-only and rendered NOTHING for the CLI engines
 * (the old DEC-8 note). That is B14: running a demo on Claude or Codex made
 * the whole right-hand panel vanish. Context stats stay builtin-only — they
 * come from a backend read the CLI engines do not populate — but the Todo
 * list now follows whichever engine is driving.
 */
import { useMemo } from "react";
import type { JSX } from "react";
import type { AppRuntime } from "../runtime/contracts/runtime";
import { useRpcQuery } from "../runtime/hooks/useRpc";
import { TodoBlock } from "./TodoBlock";
import { ContextStatsLine } from "./ContextStatsLine";
import { useEpisodeTodo } from "./useEpisodeTodo";
import { useContextStats, defaultStatsDeps, type StatsDeps } from "./useContextStats";

export function AgentStatsRegion({
  runtime,
  contextKey,
  episodeId,
  engineCurrent,
  deps,
}: {
  readonly runtime: AppRuntime;
  readonly contextKey: string;
  readonly episodeId: string | null;
  readonly engineCurrent: string | null;
  readonly deps?: StatsDeps;
}): JSX.Element | null {
  // DEC-8 stays for the stats line: only builtin turns are metered here.
  const statsEnabled = engineCurrent === "builtin";
  // D-6 (plan split-2): the Todo is a STORED record, not a replay of the
  // transcript — every engine has one, because a guest's native task tools
  // are folded into it host-side. Rendering waits only for the engine to
  // be known.
  const todoEnabled = engineCurrent !== null;
  const resolvedDeps = useMemo(
    () => deps ?? defaultStatsDeps(runtime),
    [deps, runtime],
  );
  const items = useEpisodeTodo(runtime, contextKey, episodeId, todoEnabled);
  const stats = useContextStats(runtime, episodeId, statsEnabled, resolvedDeps);

  if (!todoEnabled) return null;
  return (
    <>
      <TodoBlock items={items} />
      {statsEnabled && (
        <ContextStatsLine
          tokens={stats.tokens}
          percent={stats.percent}
          costMicros={stats.costMicros}
        />
      )}
    </>
  );
}

/**
 * AgentStatsRegionConnected — self-gating wrapper for hosting the region
 * inside the right Context panel. Resolves the current engine itself (via the
 * cached agent.get_engine query, deduped with AgentPanel's own read) so call
 * sites only pass runtime + contextKey + episodeId.
 */
export function AgentStatsRegionConnected({
  runtime,
  contextKey,
  episodeId,
}: {
  readonly runtime: AppRuntime;
  readonly contextKey: string;
  readonly episodeId: string | null;
}): JSX.Element | null {
  // Gate on agent.get_engine (pure backend read) — NOT agent.engines, which
  // proxies the sidecar and 502s when it is down.
  const { data } = useRpcQuery<{ engine: string | null }>(
    ["agent.get_engine"],
    "agent.get_engine",
    {},
    { retry: 3, retryDelay: 1000 },
  );
  // The CLI engines report progress through their own TaskCreate/TaskUpdate
  // calls, which live in the transcript rather than in episodes.todo. This
  // wrapper is the mount point the episodes module uses, so without the
  // transcript here the Todo panel is simply absent for Claude and Codex —
  // which is exactly how B14 shipped "done" while showing nothing.
  return (
    <AgentStatsRegion
      runtime={runtime}
      contextKey={contextKey}
      episodeId={episodeId}
      engineCurrent={data?.engine ?? null}
    />
  );
}
